import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MoreHorizontal,
  ArrowRight,
  Globe,
  Brain,
  Cloud,
  Shield,
  type LucideIcon,
} from "lucide-react";
import { Header } from "@/components/Header";
import { BrandLogo } from "@/components/BrandLogo";
import { projects } from "@/data/projects";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { servicesHeaderCopy } from "@/lib/siteCopy";
import { localizeProjects } from "@/lib/localizeProjects";

type NavItem = {
  label: string;
  path?: string;
  href?: string;
  id?: string;
  type: "route" | "scroll" | "external";
};

type QuickJumpItem = {
  title: string;
  subtitle: string;
  href: string;
  icon?: LucideIcon;
  accentColor?: string;
};

const serviceJumpMeta: {
  href: string;
  icon: LucideIcon;
}[] = [
  { href: "/services/web-app", icon: Globe },
  { href: "/services/ai-automation", icon: Brain },
  { href: "/services/devops-cloud", icon: Cloud },
  { href: "/services/support-maintenance", icon: Shield },
];

interface ServicesHeaderProps {
  navItems: NavItem[];
  onMobileMenuOpen?: () => void;
  /** Services category list (default) or work project list */
  variant?: "services" | "work";
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return isMobile;
}

export function ServicesHeader({
  navItems,
  onMobileMenuOpen,
  variant = "services",
}: ServicesHeaderProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const lang = useSiteLanguage();
  const t = servicesHeaderCopy[lang];
  const isWork = variant === "work";

  const serviceQuickJump: QuickJumpItem[] = serviceJumpMeta.map((meta, i) => ({
    ...meta,
    title: t.serviceJump[i].title,
    subtitle: t.serviceJump[i].subtitle,
  }));

  const workQuickJump: QuickJumpItem[] = localizeProjects(projects, lang).map(
    (project) => ({
      title: project.title,
      subtitle: project.industry,
      href: `/work/${project.id}`,
      accentColor: project.accentColor,
    }),
  );

  const quickJumpItems = isWork ? workQuickJump : serviceQuickJump;
  const quickJumpLabel = isWork ? t.ourWork : t.quickJump;
  const viewAllHref = isWork ? "/work" : "/services";
  const viewAllLabel = isWork ? t.viewAllProjects : t.viewAllServices;

  const [isQuickJumpOpen, setIsQuickJumpOpen] = useState(false);
  const [showMainHeader, setShowMainHeader] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const quickJumpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const brandMenuRef = useRef<HTMLDivElement>(null);
  const quickJumpRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (quickJumpTimerRef.current) clearTimeout(quickJumpTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isMobile || !isQuickJumpOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        brandMenuRef.current?.contains(target) ||
        quickJumpRef.current?.contains(target)
      ) {
        return;
      }
      setIsQuickJumpOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isMobile, isQuickJumpOpen]);

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openFullHeader = () => {
    cancelClose();
    setShowMainHeader(true);
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setShowMainHeader(false);
    }, 280);
  };

  const handleQuickJumpEnter = () => {
    if (isMobile) return;
    if (quickJumpTimerRef.current) clearTimeout(quickJumpTimerRef.current);
    setIsQuickJumpOpen(true);
  };

  const handleQuickJumpLeave = () => {
    if (isMobile) return;
    quickJumpTimerRef.current = setTimeout(() => setIsQuickJumpOpen(false), 200);
  };

  const handleBrandClick = () => {
    if (isMobile) {
      setIsQuickJumpOpen((open) => !open);
      return;
    }
    navigate(isWork ? "/work" : "/");
  };

  const goTo = (href: string) => {
    setIsQuickJumpOpen(false);
    setShowMainHeader(false);
    navigate(href);
  };

  const quickJumpPanelClassName =
    "p-3 bg-neutral-800/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl max-h-[min(70vh,24rem)] overflow-y-auto";

  const quickJumpContent = (
    <>
      <p className="px-2 pb-2 mb-2 text-xs font-medium uppercase tracking-wider text-white/50 border-b border-white/10">
        {quickJumpLabel}
      </p>
      <div className="space-y-1">
        {quickJumpItems.map((item, index) => (
          <motion.button
            key={item.href}
            type="button"
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.04 }}
            onClick={() => goTo(item.href)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/10 active:bg-white/15 text-left transition-colors"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{item.title}</p>
              <p className="text-xs text-white/50 truncate">{item.subtitle}</p>
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-white/30 shrink-0" />
          </motion.button>
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-white/10">
        <button
          type="button"
          onClick={() => goTo(viewAllHref)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition-colors"
        >
          {viewAllLabel}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </>
  );

  return (
    <>
      <AnimatePresence>
        {showMainHeader && (
          <motion.div
            key="full-header"
            className="fixed inset-x-0 top-0 z-[80]"
            initial={{ y: -72, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -72, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.23, 1, 0.32, 1] }}
            onMouseEnter={openFullHeader}
            onMouseLeave={scheduleClose}
          >
            <Header
              alwaysVisible
              navItems={navItems}
              onMobileMenuOpen={() => {
                setShowMainHeader(false);
                onMobileMenuOpen?.();
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!showMainHeader && (
          <motion.div
            key="compact-header"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed top-5 inset-x-0 z-[60] flex justify-center pointer-events-none"
          >
            <div className="flex items-center gap-2 pointer-events-auto">
              <div
                ref={brandMenuRef}
                className="relative"
                onMouseEnter={handleQuickJumpEnter}
                onMouseLeave={handleQuickJumpLeave}
              >
                <motion.button
                  type="button"
                  onClick={handleBrandClick}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  aria-expanded={isQuickJumpOpen}
                  aria-haspopup="true"
                  className="flex items-center gap-2.5 px-4 py-2.5 bg-neutral-600/95 dark:bg-neutral-700/95 backdrop-blur-xl text-white shadow-lg shadow-black/20 border border-white/10 rounded-xl"
                >
                  <BrandLogo
                    size="sm"
                    variant="dark"
                    textClassName="text-sm font-medium tracking-wide"
                    trademark
                  />
                </motion.button>

                <AnimatePresence>
                  {isQuickJumpOpen &&
                    (isMobile ? (
                      <div
                        key="quick-jump-mobile-anchor"
                        className="fixed inset-x-0 top-[calc(1.25rem+2.75rem+0.5rem)] z-[70] flex justify-center px-4 pointer-events-none"
                      >
                        <motion.div
                          ref={quickJumpRef}
                          initial={{ opacity: 0, y: 8, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 8, scale: 0.98 }}
                          transition={{ duration: 0.22 }}
                          className={`pointer-events-auto w-full max-w-[22rem] ${quickJumpPanelClassName}`}
                        >
                          {quickJumpContent}
                        </motion.div>
                      </div>
                    ) : (
                      <motion.div
                        key="quick-jump-desktop"
                        ref={quickJumpRef}
                        initial={{ opacity: 0, y: 8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.98 }}
                        transition={{ duration: 0.22 }}
                        className={`absolute top-full left-0 mt-2 min-w-[320px] max-w-[22rem] z-[70] ${quickJumpPanelClassName}`}
                      >
                        {quickJumpContent}
                      </motion.div>
                    ))}
                </AnimatePresence>
              </div>

              <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onMouseEnter={!isMobile ? openFullHeader : undefined}
                onMouseLeave={!isMobile ? scheduleClose : undefined}
                onClick={() => {
                  if (isMobile) {
                    setIsQuickJumpOpen(false);
                    onMobileMenuOpen?.();
                    return;
                  }
                  openFullHeader();
                }}
                className="w-11 h-11 flex items-center justify-center bg-neutral-600/95 dark:bg-neutral-700/95 backdrop-blur-xl text-white shadow-lg shadow-black/20 border border-white/10 rounded-xl"
                aria-label="Open navigation menu"
              >
                <MoreHorizontal className="w-5 h-5" />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
